import {
  createContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'pathfinder-theme';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const storedTheme = getStoredTheme();
    return storedTheme === 'system' ? getSystemTheme() : storedTheme;
  });

  // Update resolved theme when theme changes
  useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme;
    setResolvedTheme(resolved);

    // Update document class only if needed (prevents flash on mount)
    const root = document.documentElement;
    const isDarkApplied = root.classList.contains('dark');
    const shouldBeDark = resolved === 'dark';

    // Only modify classes if they don't match the desired state
    if (shouldBeDark && !isDarkApplied) {
      root.classList.remove('light');
      root.classList.add('dark');
    } else if (!shouldBeDark && isDarkApplied) {
      root.classList.remove('dark');
      root.classList.add('light');
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === 'system') {
        const newResolved = getSystemTheme();
        setResolvedTheme(newResolved);

        // Also update DOM when system theme changes
        const root = document.documentElement;
        const isDarkApplied = root.classList.contains('dark');
        const shouldBeDark = newResolved === 'dark';

        if (shouldBeDark && !isDarkApplied) {
          root.classList.remove('light');
          root.classList.add('dark');
        } else if (!shouldBeDark && isDarkApplied) {
          root.classList.remove('dark');
          root.classList.add('light');
        }
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, []);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// useTheme hook is exported from ./useTheme.ts for Fast Refresh compatibility
